import React, { useState } from "react";
import styles from "./RegisterForm.module.css";
import i18n from "../Services/i18n";
import PasswordInput from "../LayoutComponents/PasswordInput";
import AccountService from "../Services/accountService";
import LoadingSpinner from "../LayoutComponents/LoadingSpinner";
import PlanComparisonModal from "../Components/Modal/PlanComparaisonModal";
import envConfig from "../config";

const RegisterForm = () => {
  const EMAIL_RGX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    selectedPlan: "basic",
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [formRenderedAt] = useState(Date.now());
  const [showModal, setShowModal] = useState(false);

  const validate = () => {
    const newErrors = {};

    const PASSWORD_RGX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

    if (!EMAIL_RGX.test(form.email)) {
      newErrors.email = i18n.t("RegisterForm.Error.InvalidEmail");
    }

    if (!form.firstName || form.firstName.length > 128) {
      newErrors.firstName = i18n.t("RegisterForm.Error.InvalidFirstName");
    }

    if (!form.lastName || form.lastName.length > 128) {
      newErrors.lastName = i18n.t("RegisterForm.Error.InvalidLastName");
    }

    if (!PASSWORD_RGX.test(form.password)) {
      newErrors.confirmPassword = i18n.t("RegisterForm.Error.InvalidPassword");
    }

    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = i18n.t("RegisterForm.Error.PasswordMismatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handlePlanSelect = (plan) => {
    setForm({ ...form, selectedPlan: plan });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);

    try {
      const payload = {
        account: { ...form },
        formRenderedAt,
        title,
      };

      const accountId = await AccountService.CreateUserAccount(payload);
      if (!accountId) return;

      if (form.selectedPlan === "pro") {
        try {
          const locale = navigator.language?.substring(0, 2) || "en";

          const response = await fetch(
            `${envConfig.apiBaseUrl}/api/account/create-stripe-checkout`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: form.email,
                firstName: form.firstName,
                lastName: form.lastName,
                accountId: accountId,
                locale,
              }),
            },
          );

          if (!response.ok) {
            console.error("Erreur côté serveur Stripe:", await response.text());
            alert("Une erreur est survenue lors de la création du paiement.");
            return;
          }

          const { checkoutUrl } = await response.json();

          if (!checkoutUrl) {
            alert("Erreur : aucun checkoutUrl reçu du serveur.");
            return;
          }

          // Redirection vers la page de paiement Stripe
          window.location.href = checkoutUrl;
        } catch (err) {
          console.error("Erreur Stripe checkout:", err);
          alert("Une erreur est survenue lors de la redirection vers Stripe.");
        }
      } else {
        // Plan basic → compte créé sans paiement, tu peux rediriger où tu veux
        window.location.href = "/accountcreated";
      }
    } catch (err) {
      console.error(err);
      alert("Une erreur est survenue lors de la création du compte.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {isSaving && (
        <div className={styles.savingOverlay}>
          <LoadingSpinner />
        </div>
      )}
      <h2>{i18n.t("RegisterForm.Title")}</h2>

      <div style={{ display: "none" }}>
        <label>
          Title
          <input
            type="text"
            name="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoComplete="off"
          />
        </label>
      </div>

      <input type="hidden" name="formRenderedAt" value={formRenderedAt} />

      <div className={styles.inputGroup}>
        <label>
          {i18n.t("RegisterForm.Email")}
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            maxLength={512}
            required
          />
        </label>
        {errors.email && <div className={styles.errorText}>{errors.email}</div>}
      </div>

      <div className={styles.inlineInputs}>
        <label>
          {i18n.t("RegisterForm.FirstName")}
          <input
            type="text"
            name="firstName"
            maxLength={128}
            value={form.firstName}
            onChange={handleChange}
            required
          />
        </label>

        <label>
          {i18n.t("RegisterForm.LastName")}
          <input
            type="text"
            name="lastName"
            maxLength={128}
            value={form.lastName}
            onChange={handleChange}
            required
          />
        </label>
      </div>
      {errors.firstName && <div className={styles.errorText}>{errors.firstName}</div>}
      {errors.lastName && <div className={styles.errorText}>{errors.lastName}</div>}

      <div className={styles.inlineInputs}>
        <label>
          {i18n.t("RegisterForm.Password")}
          <PasswordInput name="password" value={form.password} onChange={handleChange} />
        </label>

        <label>
          {i18n.t("RegisterForm.ConfirmPassword")}
          <PasswordInput
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
          />
        </label>
      </div>
      {errors.confirmPassword && <div className={styles.errorText}>{errors.confirmPassword}</div>}

      <div className={styles.planSelection}>
        <h3>{i18n.t("RegisterForm.ChoosePlan")}</h3>
        <p>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            style={{
              background: "none",
              border: "none",
              color: "var(--primary)",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "0.9rem",
              marginLeft: "0.5rem",
            }}>
            Voir la comparaison détaillée
          </button>
        </p>

        {showModal && <PlanComparisonModal onClose={() => setShowModal(false)} />}
        <div className={styles.planCards}>
          <div
            className={`${styles.planCard} ${form.selectedPlan === "basic" ? styles.selected : ""}`}
            onClick={() => handlePlanSelect("basic")}>
            {form.selectedPlan === "basic" && <div className={styles.selectedCheck}>✓</div>}
            <h4>Basic</h4>
            <p className={styles.price}>Gratuit</p>
            <p>{i18n.t("RegisterPage.BasicDesc")}</p>
            <ul>
              <li>{i18n.t("RegisterPage.BasicFeature1")}</li>
              <li>{i18n.t("RegisterPage.BasicFeature2")}</li>
              <li>{i18n.t("RegisterPage.BasicFeature3")}</li>
            </ul>
          </div>
          <div
            className={`${styles.planCard} ${form.selectedPlan === "pro" ? styles.selected : ""} ${
              styles.premium
            } `}
            /*className={`${styles.planCard} ${form.selectedPlan === "pro" ? styles.selected : ""} ${
              styles.premium
            } ${styles.disabled}`}*/
            onClick={() => {
              handlePlanSelect("pro");
            }}>
            {form.selectedPlan === "pro" && <div className={styles.selectedCheck}>✓</div>}
            {/*<div className={styles.disabledOverlay}>⛔ {i18n.t("RegisterPage.Unavailable")}</div>*/}
            <h4>Professionnel</h4>
            <p className={styles.price}>30,00 $ CAD/mois</p>
            <p>{i18n.t("RegisterPage.ProDesc")}</p>
            <ul>
              <li>{i18n.t("RegisterPage.ProFeature1")}</li>
              <li>{i18n.t("RegisterPage.ProFeature2")}</li>
              <li>{i18n.t("RegisterPage.ProFeature3")}</li>
            </ul>
          </div>
        </div>
      </div>

      <button type="submit" className={styles.submitButton}>
        {i18n.t("RegisterForm.Submit")}
      </button>
      {/* ➕ Nouvelle section pour les usagers déjà inscrits */}
      <div className={styles.loginRedirect}>
        <span>{i18n.t("RegisterForm.AlreadyMember")} </span>
        <a href="/login" className={styles.loginLink}>
          {i18n.t("RegisterForm.GoToLogin")}
        </a>
      </div>
    </form>
  );
};

export default RegisterForm;
